import React, { useState, useContext, useRef, useEffect } from 'react';
import { StoryWrapper } from '../StoryWrapper';
import styles from '../week-reflection.module.css';
import { WeekReflectionContext } from '../WeekReflectionContext';
import ShareButton from '../../share/ShareButton';

export const ReflectionInput = ({ story, action, isPaused, config, storyData }) => {
    const [hasTextChanged, setHasTextChanged] = useState(false);
    const textareaRef = useRef(null);
    const { textInputs, setTextInput, setInputChange, reflectionId, setReflectionId,
        weekReviewManager, handleRestart } = useContext(WeekReflectionContext);
    const [originalAnswer, setOriginalAnswer] = useState(textInputs[storyData.component] ?? '');
    const [isSaved, setIsSaved] = useState(false);
    const isSaving = useRef(false);
    const [saveButtonText, setSaveButtonText] = useState('Save Reflection');

    const onInput = (e) => {
        // update the value in context
        setTextInput(prevInput => ({
            ...prevInput,
            [storyData.component]: e.target.value
        }))

        if (e.target.value !== originalAnswer) {
            setHasTextChanged(true);
        }
        else {
            setHasTextChanged(false);
        }
    }

    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = "auto"; // Reset first
            textarea.style.height = Math.min(textarea.scrollHeight, 320) + "px"; // 320px = 20rem
        }
    }, [textInputs[storyData.component]])

    const debouncedOnClick = async (e) => {
        if (isSaving.current) return;
        isSaving.current = true;
        try {
            await onClick(e);
        }
        finally {
            setTimeout(() => {
                isSaving.current = false;
            }, 400); // 400ms debounce
        }
    };

    const onClick = async () => {
        if (hasTextChanged) {
            setInputChange(true);
        }
        const documentId = await weekReviewManager?.saveReflection(textInputs);
        if (documentId) {
            setIsSaved(true);
            setOriginalAnswer(textInputs[storyData.component]);
            setHasTextChanged(false);

            if (!reflectionId) {
                // if our context didn't have an id for the reflection being worked on, then set it.
                setReflectionId(documentId);
            }
        }
        else {
            setSaveButtonText('Oops try again');
        }
    }

    const isShareButtonDisplayed = () => {
        if (!hasTextChanged) {
            // i.e. we have reflection id from a previous save, or it was saved now then allow share
            if (reflectionId || isSaved) {
                return true;
            }
        }

        return false;
    }

    return (
        <>
            <StoryWrapper storyName={storyData.component} action={action} isPaused={isPaused} areCommentsEnabled={false}
                showPauseButton={false} handleRestart={handleRestart}>
                <h1 className={styles.size3} style={{ margin: "1rem" }}>
                    {storyData.data.question}
                </h1>
                <div className={styles.reflectionInputDiv}>
                    <textarea
                        ref={textareaRef}
                        className={styles.reflectionTextbox}
                        spellCheck="false" placeholder={"..."}
                        onChange={onInput}
                        value={textInputs.reflectionInput ?? ''}
                        readOnly={story?.view === 'readOnly'}
                    />
                </div>
                {story?.view !== 'readOnly' && // don't show buttons when viewing shared reflection
                    <div style={{ marginTop: "2.5rem", zIndex: 1000 }}>
                        {isShareButtonDisplayed() ?
                            <ShareButton entryId={reflectionId} shareType="story" />
                            : <button className={styles.saveButton}
                                onClick={debouncedOnClick}
                                onTouchEnd={debouncedOnClick}
                                style={{ zIndex: 1000 }}
                                disabled={textInputs.reflectionInput?.length === 0 || !hasTextChanged}
                            >
                                {saveButtonText}
                            </button>
                        }
                    </div>
                }
            </StoryWrapper>
        </>
    );
}